import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import AdminLayout from "../components/AdminLayout";
import MapEditor, { darkTheme } from "./Map";
import { db } from "../firebase";
import {
  addDoc,
  collection,
  deleteField,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Map as MapIcon,
} from "@mui/icons-material";

const MapContainer = styled("div")`
  display: flex;
  flex-direction: column;
  gap: var(--space-xl);
`;

const MapHeader = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-lg);
  flex-wrap: wrap;
  gap: var(--space-md);
`;

const PageTitle = styled("h1")`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: var(--space-md);

  svg {
    width: 40px;
    height: 40px;
    color: var(--accent-primary);
  }
`;

const PageSubtitle = styled("p")`
  font-size: 1rem;
  color: var(--text-secondary);
  margin: 0;
  font-weight: 400;
`;

export default function MapList() {
  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMapId, setCurrentMapId] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, mapId: null, mapName: "" });
  const [newMapDialog, setNewMapDialog] = useState({ open: false, name: "" });
  const [activating, setActivating] = useState(null);
  const [creating, setCreating] = useState(false);
  const [activateDialog, setActivateDialog] = useState({ open: false, mapId: null, mapName: "" });

  // Subscribe to store_maps collection
  useEffect(() => {
    const q = query(collection(db, "store_maps"), orderBy("created_at", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setMaps(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  const showSnack = (msg, severity = "success") =>
    setSnack({ open: true, msg, severity });

  const handleCreate = async () => {
    const name = newMapDialog.name.trim() || `Map ${maps.length + 1}`;
    setCreating(true);
    try {
      const isFirst = maps.length === 0;
      const newRef = await addDoc(collection(db, "store_maps"), {
        name,
        image_url: "",
        is_active: isFirst,
        nodes: [],
        edges: [],
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
      if (isFirst) {
        await setDoc(doc(db, "settings", "active_map"), {
          map_id: newRef.id,
          updated_at: serverTimestamp(),
        });
      }
      setNewMapDialog({ open: false, name: "" });
      setCurrentMapId(newRef.id);
    } catch (e) {
      showSnack(e.message, "error");
    } finally {
      setCreating(false);
    }
  };

  const handleSetActive = async (mapId) => {
    setActivateDialog({ open: false, mapId: null, mapName: "" });
    setActivating(mapId);
    try {
      const allMaps = await getDocs(collection(db, "store_maps"));
      const batch = writeBatch(db);
      allMaps.docs.forEach((d) =>
        batch.update(d.ref, { is_active: d.id === mapId })
      );
      batch.set(doc(db, "settings", "active_map"), {
        map_id: mapId,
        updated_at: serverTimestamp(),
      });
      await batch.commit();
      showSnack("Active map updated — mobile app will now use this map.");
    } catch (e) {
      showSnack(e.message, "error");
    } finally {
      setActivating(null);
    }
  };

  const handleDelete = async () => {
    const { mapId } = deleteDialog;
    setDeleteDialog({ open: false, mapId: null, mapName: "" });
    try {
      const productsSnap = await getDocs(collection(db, "products"));
      const batch = writeBatch(db);
      productsSnap.docs.forEach((d) => {
        if (d.data().map_coords?.[mapId]) {
          batch.update(d.ref, { [`map_coords.${mapId}`]: deleteField() });
        }
      });
      batch.delete(doc(db, "store_maps", mapId));
      await batch.commit();
      showSnack("Map deleted.");
    } catch (e) {
      showSnack(e.message, "error");
    }
  };

  // When editing, show MapEditor inside the same layout
  if (currentMapId) {
    return (
      <ThemeProvider theme={darkTheme}>
        <AdminLayout>
          <MapEditor mapId={currentMapId} onBack={() => setCurrentMapId(null)} />
        </AdminLayout>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <AdminLayout>
        <MapContainer>
          <MapHeader>
            <div>
              <PageTitle>
                <MapIcon />
                Store Maps
              </PageTitle>
              <PageSubtitle>
                Manage multiple store layouts. One map is active for the mobile app at a time.
              </PageSubtitle>
            </div>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setNewMapDialog({ open: true, name: "" })}
            >
              New Map
            </Button>
          </MapHeader>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : maps.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <MapIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No maps yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first store map to get started.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setNewMapDialog({ open: true, name: "" })}
              >
                Create First Map
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {/* Active map first, then the rest */}
              {[...maps].sort((a, b) => (b.is_active ? 1 : 0) - (a.is_active ? 1 : 0)).map((map) => (
                <Box
                  key={map.id}
                  sx={{
                    width: {
                      xs: "100%",
                      sm: "calc(50% - 8px)",
                      md: "calc(33.33% - 11px)",
                    },
                  }}
                >
                  <Card
                    sx={{
                      background: "var(--surface)",
                      border: map.is_active
                        ? "2px solid #22c55e"
                        : "1px solid var(--border)",
                      boxShadow: map.is_active
                        ? "0 0 0 3px rgba(34,197,94,0.18), 0 4px 24px rgba(34,197,94,0.12)"
                        : "none",
                      borderRadius: "var(--radius-lg)",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "box-shadow 0.2s",
                    }}
                  >
                    {/* Thumbnail */}
                    <Box
                      sx={{
                        height: 150,
                        background: "#0d1a0d",
                        overflow: "hidden",
                        position: "relative",
                        flexShrink: 0,
                      }}
                    >
                      {map.image_url ? (
                        <img
                          src={map.image_url}
                          alt={map.name}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                          <MapIcon sx={{ fontSize: 48, color: "#333" }} />
                        </Box>
                      )}

                      {/* Full-width active banner at bottom of thumbnail */}
                      {map.is_active && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: "rgba(34,197,94,0.88)",
                            color: "#fff",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            textAlign: "center",
                            py: 0.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                          }}
                        >
                          <CheckCircleIcon sx={{ fontSize: 13 }} />
                          ACTIVE — IN USE BY MOBILE APP
                        </Box>
                      )}
                    </Box>

                    <CardContent sx={{ flex: 1, pb: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                          {map.name}
                        </Typography>
                        {map.is_active && (
                          <Chip
                            label="Active"
                            size="small"
                            color="success"
                            sx={{ fontWeight: 700, fontSize: "0.68rem" }}
                          />
                        )}
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                        <Chip size="small" label={`${map.nodes?.length ?? 0} nodes`} />
                        <Chip size="small" label={`${map.edges?.length ?? 0} edges`} />
                      </Stack>
                    </CardContent>

                    <CardActions sx={{ px: 2, pb: 2, gap: 1, flexWrap: "wrap" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => setCurrentMapId(map.id)}
                      >
                        Edit
                      </Button>
                      {!map.is_active && (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          disabled={activating === map.id}
                          startIcon={
                            activating === map.id
                              ? <CircularProgress size={14} color="inherit" />
                              : <CheckCircleIcon />
                          }
                          onClick={() =>
                            setActivateDialog({ open: true, mapId: map.id, mapName: map.name })
                          }
                        >
                          Set Active
                        </Button>
                      )}
                      <Box sx={{ flex: 1 }} />
                      {!map.is_active && (
                        <Tooltip title="Delete map and all its product pins">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setDeleteDialog({ open: true, mapId: map.id, mapName: map.name })
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </CardActions>
                  </Card>
                </Box>
              ))}
            </Box>
          )}
        </MapContainer>

        {/* New Map Dialog */}
        <Dialog
          open={newMapDialog.open}
          onClose={() => setNewMapDialog({ open: false, name: "" })}
        >
          <DialogTitle>Create New Map</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              size="small"
              fullWidth
              label="Map Name"
              placeholder={`Map ${maps.length + 1}`}
              value={newMapDialog.name}
              onChange={(e) =>
                setNewMapDialog((s) => ({ ...s, name: e.target.value }))
              }
              onKeyDown={(e) =>
                e.key === "Enter" && !creating && handleCreate()
              }
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNewMapDialog({ open: false, name: "" })}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={creating}
            >
              {creating ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                "Create & Edit"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Set Active Confirm Dialog */}
        <Dialog
          open={activateDialog.open}
          onClose={() => setActivateDialog({ open: false, mapId: null, mapName: "" })}
        >
          <DialogTitle>Switch active map?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <strong>"{activateDialog.mapName}"</strong> will immediately become the active map.
              The mobile app will start using it right away. Make sure the route graph is ready before switching.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActivateDialog({ open: false, mapId: null, mapName: "" })}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleSetActive(activateDialog.mapId)}
            >
              Yes, Set Active
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirm Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() =>
            setDeleteDialog({ open: false, mapId: null, mapName: "" })
          }
        >
          <DialogTitle>Delete "{deleteDialog.mapName}"?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              All product pins for this map will be permanently removed. This
              cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setDeleteDialog({ open: false, mapId: null, mapName: "" })
              }
            >
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snack.open}
          autoHideDuration={4000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snack.severity} sx={{ width: "100%" }}>
            {snack.msg}
          </Alert>
        </Snackbar>
      </AdminLayout>
    </ThemeProvider>
  );
}
